import React from "react";
import { useForm } from "../FormContext";
import type { InputFieldProps } from "../../../interfaces/components/form";

export const InputFieldComponent: React.FC<InputFieldProps> = ({
  name,
  value,
  onChange,
  onBlur,
  type = "text",
  placeholder,
  required,
  className = "",
  mask,
}) => {
  const { setFieldValue, setFieldTouched, validateField, fields } = useForm();
  const fieldValue = fields[name]?.value || value || "";
  const fieldErrors = fields[name]?.errors || [];
  const hasError = fieldErrors.length > 0;

  // Function to apply mask to input value
  const applyMask = (value: string, maskPattern: string): string => {
    if (!maskPattern) return value;

    let result = "";
    let valueIndex = 0;

    for (let i = 0; i < maskPattern.length && valueIndex < value.length; i++) {
      const maskChar = maskPattern[i];
      const valueChar = value[valueIndex];

      if (maskChar === "X") {
        // X represents any digit
        if (/[0-9]/.test(valueChar)) {
          result += valueChar;
          valueIndex++;
        }
      } else if (maskChar === "A") {
        // A represents any letter
        if (/[a-zA-Z]/.test(valueChar)) {
          result += valueChar;
          valueIndex++;
        }
      } else if (maskChar === "*") {
        // * represents any character
        result += valueChar;
        valueIndex++;
      } else {
        // Static character from mask
        result += maskChar;
        if (valueChar === maskChar) {
          valueIndex++;
        }
      }
    }

    return result;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Apply mask if provided
    if (mask) {
      newValue = applyMask(newValue, mask);
    }

    setFieldValue(name, newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
    validateField(name);
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <input
      type={type}
      value={fieldValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      required={required}
      className={`w-full px-2 py-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 bg-transparent focus:ring-blue-500 dark:text-white border
        ${hasError ? "border-red-500" : "border-gray-300 dark:border-gray-700"}
        ${className}`}
    />
  );
};
